import { useEffect, useMemo, useState } from 'react';
import supabase from '../../../../../utils/supabase';
import { normalizeRank } from '../taxonomy/normalize';
import { resolveFamilyName, resolveSubfamilyName } from '../taxonomy/resolve';

export const useTaxonomyData = (t) => {
  const [rawData, setRawData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Use RPC to fetch data with resolved hierarchy and fossil status
        const { data: allData, error: rpcError } = await supabase.rpc('get_taxa_concepts');
        
        if (rpcError) throw rpcError;

        if (!active) return;
        setRawData(allData || []);
      } catch (fetchError) {
        console.error('Failed to fetch taxa trends:', fetchError);
        if (active) setError(t('error.fetchMessage'));
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchData();

    return () => {
      active = false;
    };
  }, [t]);

  const recordMap = useMemo(() => {
    if (!rawData.length) return new Map();
    return new Map(rawData.map(item => [item.id, item]));
  }, [rawData]);

  const familyCache = useMemo(() => new Map(), [rawData]);
  const subfamilyCache = useMemo(() => new Map(), [rawData]);

  const rankOptions = useMemo(() => {
    if (!rawData.length) return [];
    const ranks = [...new Set(rawData
      .map(item => normalizeRank(item.current_rank))
      .filter(Boolean))].sort();
    return ['All', ...ranks];
  }, [rawData]);

  const unitOptions = useMemo(() => {
    if (!rawData.length) return [{ value: 'All', label: t('common:all') }];
    const families = new Set();
    const subfamilies = new Set();
    rawData.forEach((item) => {
      const family = item.family || resolveFamilyName(item.id, recordMap, familyCache);
      const subfamily = item.subfamily || resolveSubfamilyName(item.id, recordMap, subfamilyCache);
      if (family) families.add(family);
      if (subfamily) subfamilies.add(subfamily);
    });

    const familyOptions = Array.from(families).sort().map(name => ({
      value: `family::${name}`,
      label: `${t('common:family')}: ${name}`,
    }));
    const subfamilyOptions = Array.from(subfamilies).sort().map(name => ({
      value: `subfamily::${name}`,
      label: `${t('common:subfamily')}: ${name}`,
    }));

    return [{ value: 'All', label: t('common:all') }, ...familyOptions, ...subfamilyOptions];
  }, [rawData, recordMap, familyCache, subfamilyCache, t]);

  const yearRange = useMemo(() => {
    if (!rawData.length) return [0, 0];
    const years = rawData.map(item => Number(item.authority_year)).filter(Boolean);
    if (!years.length) return [0, 0];
    return [Math.min(...years), Math.max(...years)];
  }, [rawData]);

  return {
    rawData,
    loading,
    error,
    rankOptions,
    unitOptions,
    recordMap,
    familyCache,
    subfamilyCache,
    yearRange,
  };
};
